import axios from "axios";
import { RunConfigService } from "@nuskin/ns-util";

/**
 * @typedef ProductData
 * @property {string} id
 * @property {string} title
 * @property {object[]} [productImages]
 * @property {string} [productImages.url]
 * @property {string} [productImages.alt]
 * @property {string} [slug]
 * @property {string} [salesLabel]
 * @property {object[]} [variants]
 * @property {string} [variants.sku]
 * @property {string} [variants.variantLabel]
 * @property {boolean} [variants.isExclusive]
 * @property {number} [variants.availableQuantity]
 * @property {object[]} [variants.productImages]
 * @property {string} [variants.productImages.url]
 * @property {string} [variants.productImages.alt]
 * @property {string} [variants.pricingJson]
 * @property {object} [variants.price]
 * @property {string} [variants.price.currencyCode]
 * @property {number} [variants.price.retail]
 * @property {number} [variants.price.retailSales]
 * @property {number} [variants.price.retailSubscription]
 * @property {number} [variants.price.wholesale]
 * @property {number} [variants.price.wholesaleSales]
 * @property {number} [variants.price.wholesaleSubscription]
 * @property {object} [status]
 * @property {'RELEASED_FOR_SALE'|'NOT_RELEASED_FOR_SALE'|'DISCONTINUED'|'STOP_STATUS'|'NOT_FOUND'|'NOT_SOLD_SEPERATELY'|'inactive'|'sellable'|'preview'|'discontinued'|'stopped'|'replacement'|'bundleOnly'|'test'} [status.status]
 * @property {boolean} [status.isBackordered]
 * @property {string} [status.backorderedAvailableDate]
 */

const ProductsByIdQuery = `
query ProductsById($ids: [String!]!, $market: String, $language: String) {
  productsById(ids: $ids, market: $market, language: $language, quantity: 1) {
    products {
      id
      title
      productImages {
        url
        alt
      }
      slug
      salesLabel
      variants {
        sku
        variantLabel
        isExclusive
        availableQuantity
        productImages {
          url
          alt
        }
        pricingJson
        price {
          currencyCode
          retail
          retailSales
          retailSubscription
          wholesale
          wholesaleSales
          wholesaleSubscription
        }
        status {
          status
          isBackordered
          backorderedAvailableDate
        }
        primaryBrand
      }
    }
  }
}
`;

/**
 * @param {string[]} fields
 * @returns {string}
 */
const productTranslationsQuery = fields => `
  query ProductTranslationsQuery($market: String, $language: String) {
    pdpStrings(market: $market, language: $language) {
      ${fields.join("\n")}
    }
  }
`;

function getProductGraphqlUrl() {
  /** @type {'dev'|'test'|'stage'|'prod'|'unknown'} */
  const env = RunConfigService.getEnvironmentCode();

  switch (env) {
    case "dev":
      return "https://apis.dev.nuskin.com/product/graphql";
    case "stage":
    case "test":
      return "https://apis.test.nuskin.com/product/graphql";
    case "unknown":
    case "prod":
    default:
      return "https://apis.nuskin.com/product/graphql";
  }
}

/**
 * Retrieves product data for the given SKUs and/or product UIDs
 * @param {string[]} ids Product IDs; can be SKUs and/or UIDs
 * @param {string} market Market country code
 * @param {string} language Market language code
 * @return {Promise<ProductData[]>}
 */
export async function findProductsById(ids, market, language) {
  const url = getProductGraphqlUrl();

  const response = await axios.post(url, {
    query: ProductsByIdQuery,
    variables: { ids, market, language }
  });

  if (!response.data || !response.data.data) return [];

  return response.data.data.productsById.products;
}

/**
 * @param {string[]} fields Product string fields to get
 * @param {string} market Market country code
 * @param {string} language Market language code
 */
export async function getProductTranslations(fields, market, language) {
  // If no fields are specified, don't bother attempting an API query
  if (fields.length === 0) return {};

  const url = getProductGraphqlUrl();
  const query = productTranslationsQuery(fields);

  const response = await axios.post(url, {
    query,
    variables: { market, language }
  });

  if (!response.data || !response.data.data) return {};
  return response.data.data.pdpStrings || {};
}
